import SupportTicketsApi from '../../api/supportTickets';
import camelcaseKeys from 'camelcase-keys';

const DEFAULT_PER_PAGE = 15;

const state = {
  tickets: [],
  selectedTicketId: null,
  listLoading: false,
  detailLoading: false,
  messagesLoading: false,
  listMeta: {
    count: 0,
    currentPage: 1,
    perPage: DEFAULT_PER_PAGE,
  },
  listFilters: {
    q: '',
    status: 'all',
    priority: 'all',
    assigneeId: 'all',
    hasComments: 'all',
    dueDate: '',
  },
};

const getters = {
  getSupportTickets: _state => _state.tickets,
  getSupportTicketsMeta: _state => _state.listMeta,
  getSupportTicketFilters: _state => _state.listFilters,
  getSelectedSupportTicket: _state => {
    if (!_state.selectedTicketId) return {};
    return (
      _state.tickets.find(
        t => Number(t.id) === Number(_state.selectedTicketId)
      ) || {}
    );
  },
  getSupportTicketListLoading: _state => _state.listLoading,
  getSupportTicketDetailLoading: _state => _state.detailLoading,
  getSupportMessagesLoading: _state => _state.messagesLoading,
};

const mutations = {
  SET_SUPPORT_TICKETS(_state, tickets) {
    _state.tickets = tickets;
  },
  SET_SUPPORT_TICKETS_META(_state, meta) {
    _state.listMeta = { ..._state.listMeta, ...meta };
  },
  SET_SUPPORT_TICKET_FILTERS(_state, filters) {
    _state.listFilters = { ..._state.listFilters, ...filters };
  },
  SET_SUPPORT_TICKET_LIST_LOADING(_state, status) {
    _state.listLoading = status;
  },
  SET_SUPPORT_TICKET_DETAIL_LOADING(_state, status) {
    _state.detailLoading = status;
  },
  SET_SUPPORT_MESSAGES_LOADING(_state, status) {
    _state.messagesLoading = status;
  },
  SET_SELECTED_SUPPORT_TICKET(_state, ticketId) {
    _state.selectedTicketId = ticketId;
  },
  CLEAR_SELECTED_SUPPORT_TICKET(_state) {
    _state.selectedTicketId = null;
  },
  UPSERT_SUPPORT_TICKET(_state, ticket) {
    const index = _state.tickets.findIndex(
      t => Number(t.id) === Number(ticket.id)
    );
    if (index === -1) {
      _state.tickets = [ticket, ..._state.tickets];
      _state.listMeta.count += 1;
    } else {
      const existing = _state.tickets[index];
      const incomingMessages = ticket.messages || [];
      let messages = existing.messages;
      if (!existing.dataFetched && incomingMessages.length) {
        messages = incomingMessages;
      }
      _state.tickets[index] = {
        ...existing,
        ...ticket,
        messages,
        dataFetched: existing.dataFetched,
      };
    }
  },
  SET_SUPPORT_TICKET_MESSAGES(_state, { ticketId, messages }) {
    const ticket = _state.tickets.find(t => Number(t.id) === Number(ticketId));
    if (ticket) {
      ticket.messages = messages;
      ticket.dataFetched = true;
    }
  },
  ADD_SUPPORT_TICKET_MESSAGE(_state, { ticketId, message }) {
    const ticket = _state.tickets.find(t => Number(t.id) === Number(ticketId));
    if (!ticket) return;

    const messages = ticket.messages || [];
    if (!messages.some(m => m.id === message.id)) {
      ticket.messages = [...messages, message];
    }
    ticket.commentsCount = (ticket.commentsCount || 0) + 1;
    ticket.lastActivityAt = message.createdAt;
  },
};

const buildListParams = (moduleState, overrides = {}) => {
  const filters = { ...moduleState.listFilters, ...overrides };
  const { currentPage, perPage } = { ...moduleState.listMeta, ...overrides };

  return {
    page: currentPage,
    perPage,
    q: filters.q || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    priority: filters.priority === 'all' ? undefined : filters.priority,
    assigneeId: filters.assigneeId === 'all' ? undefined : filters.assigneeId,
    hasComments:
      filters.hasComments === 'all' ? undefined : filters.hasComments,
    dueDate: filters.dueDate || undefined,
  };
};

const actions = {
  fetchSupportTickets: async (
    { commit, state: moduleState },
    overrides = {}
  ) => {
    commit('SET_SUPPORT_TICKET_LIST_LOADING', true);
    try {
      const params = buildListParams(moduleState, overrides);
      const { data } = await SupportTicketsApi.getAll(params);
      const tickets = camelcaseKeys(data.data.payload, { deep: true });
      const meta = camelcaseKeys(data.data.meta, { deep: true });
      commit('SET_SUPPORT_TICKETS', tickets);
      commit('SET_SUPPORT_TICKETS_META', {
        count: meta.count,
        currentPage: Number(params.page) || 1,
        perPage: params.perPage,
      });
    } finally {
      commit('SET_SUPPORT_TICKET_LIST_LOADING', false);
    }
  },

  setListFilters: async ({ commit, dispatch }, filters) => {
    commit('SET_SUPPORT_TICKET_FILTERS', filters);
    commit('SET_SUPPORT_TICKETS_META', { currentPage: 1 });
    await dispatch('fetchSupportTickets');
  },

  setListPage: async ({ commit, dispatch }, page) => {
    commit('SET_SUPPORT_TICKETS_META', { currentPage: page });
    await dispatch('fetchSupportTickets');
  },

  setListPerPage: async ({ commit, dispatch }, perPage) => {
    commit('SET_SUPPORT_TICKETS_META', { perPage, currentPage: 1 });
    await dispatch('fetchSupportTickets');
  },

  fetchSupportTicket: async ({ dispatch }, ticketId) => {
    await dispatch('loadSupportTicketDetail', ticketId);
  },

  loadSupportTicketDetail: async ({ commit, dispatch }, ticketId) => {
    const normalizedId = Number(ticketId);
    commit('SET_SUPPORT_TICKET_DETAIL_LOADING', true);
    try {
      const { data } = await SupportTicketsApi.show(normalizedId);
      const ticket = camelcaseKeys(data, { deep: true });
      commit('UPSERT_SUPPORT_TICKET', {
        ...ticket,
        messages: [],
        dataFetched: false,
      });
      commit('SET_SELECTED_SUPPORT_TICKET', normalizedId);
      await dispatch('fetchSupportTicketMessages', {
        ticketId: normalizedId,
        force: true,
      });
    } finally {
      commit('SET_SUPPORT_TICKET_DETAIL_LOADING', false);
    }
  },

  createSupportTicket: async ({ commit, dispatch }, payload) => {
    const { data } = await SupportTicketsApi.create(payload);
    const ticket = camelcaseKeys(data, { deep: true });
    commit('UPSERT_SUPPORT_TICKET', ticket);
    await dispatch('fetchSupportTickets');
    return ticket;
  },

  updateSupportTicket: async ({ commit }, { ticketId, ...payload }) => {
    const { data } = await SupportTicketsApi.update(ticketId, payload);
    const ticket = camelcaseKeys(data, { deep: true });
    commit('UPSERT_SUPPORT_TICKET', ticket);
    return ticket;
  },

  reopenSupportTicket: async ({ commit }, ticketId) => {
    const { data } = await SupportTicketsApi.reopen(ticketId);
    const ticket = camelcaseKeys(data, { deep: true });
    commit('UPSERT_SUPPORT_TICKET', ticket);
    return ticket;
  },

  fetchSupportTicketMessages: async (
    { commit, state: moduleState },
    { ticketId, force = false } = {}
  ) => {
    const normalizedId = Number(ticketId);
    const ticket = moduleState.tickets.find(t => Number(t.id) === normalizedId);
    if (ticket?.dataFetched && !force) return;

    commit('SET_SUPPORT_MESSAGES_LOADING', true);
    try {
      const { data } = await SupportTicketsApi.getMessages(normalizedId);
      const messages = camelcaseKeys(data.payload, { deep: true });
      commit('SET_SUPPORT_TICKET_MESSAGES', {
        ticketId: normalizedId,
        messages,
      });
    } finally {
      commit('SET_SUPPORT_MESSAGES_LOADING', false);
    }
  },

  sendSupportTicketMessage: async ({ commit }, { ticketId, content }) => {
    const { data } = await SupportTicketsApi.createMessage({
      ticketId,
      content,
    });
    const message = camelcaseKeys(data, { deep: true });
    commit('ADD_SUPPORT_TICKET_MESSAGE', { ticketId, message });
    return message;
  },

  setActiveSupportTicket: async ({ commit, dispatch }, ticket) => {
    commit('SET_SELECTED_SUPPORT_TICKET', Number(ticket.id));
    await dispatch('fetchSupportTicketMessages', {
      ticketId: ticket.id,
      force: false,
    });
  },

  clearActiveSupportTicket({ commit }) {
    commit('CLEAR_SELECTED_SUPPORT_TICKET');
  },

  handleSupportTicketMessageCreated(
    { commit, dispatch, state: moduleState },
    data
  ) {
    const message = camelcaseKeys(data.support_ticket_message, { deep: true });
    const ticketId = message.supportTicketId;

    const ticket = moduleState.tickets.find(
      t => Number(t.id) === Number(ticketId)
    );
    if (!ticket) {
      dispatch('fetchSupportTickets');
      return;
    }

    commit('ADD_SUPPORT_TICKET_MESSAGE', { ticketId, message });
    if (data.last_activity_at) {
      commit('UPSERT_SUPPORT_TICKET', {
        ...ticket,
        lastActivityAt: data.last_activity_at,
      });
    }
  },

  handleSupportTicketUpdated({ commit }, data) {
    const ticket = camelcaseKeys(data, { deep: true });
    commit('UPSERT_SUPPORT_TICKET', ticket);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
